import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { TriangleAlert } from "lucide-react";
import { getApp, updateApp, type UpdateAppInput } from "../api";
import AppForm, { AppFormData } from "../components/AppForm";
import PageHeader from "../components/PageHeader";
import { useToast } from "../components/Toast";
import { usePageTitle } from "../hooks/usePageTitle";

export default function EditAppPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [appName, setAppName] = useState<string | null>(null);

  usePageTitle(appName ? `Edit ${appName}` : "Edit App");

  const [formData, setFormData] = useState<AppFormData>({
    name: "",
    description: "",
    shopifyDashboardUrl: "",
    clientId: "",
    clientSecret: "",
    scopes: "",
  });

  useEffect(() => {
    if (id) {
      loadApp(id);
    }
  }, [id]);

  async function loadApp(appId: string) {
    try {
      setLoading(true);
      const app = await getApp(appId);
      setAppName(app.name);
      setFormData({
        name: app.name,
        description: app.description || "",
        shopifyDashboardUrl: app.shopifyDashboardUrl || "",
        clientId: app.clientId,
        clientSecret: "", // Don't pre-fill secret
        scopes: app.scopes.split(",").join(", "),
      });
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load app");
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;

    setSaving(true);
    setError(null);

    try {
      // Only send non-empty fields (description can be cleared with null)
      const updates: UpdateAppInput = {};
      if (formData.name) updates.name = formData.name;
      updates.description = formData.description || null;
      updates.shopifyDashboardUrl = formData.shopifyDashboardUrl || null;
      if (formData.clientId) updates.clientId = formData.clientId;
      if (formData.clientSecret) updates.clientSecret = formData.clientSecret;
      if (formData.scopes) updates.scopes = formData.scopes;

      await updateApp(id, updates);
      addToast("App updated successfully", "success");
      navigate(`/apps/${id}`);
    } catch (err) {
      // Show error inline in form, not as toast (avoid duplicate)
      setError(err instanceof Error ? err.message : "Failed to update app");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-dark-text-secondary">Loading...</div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader>
        <PageHeader.Breadcrumb to={`/apps/${id}`} label="Back" />
        <PageHeader.Title>Edit App</PageHeader.Title>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AppForm
          formData={formData}
          onChange={setFormData}
          onSubmit={handleSubmit}
          loading={saving}
          error={error}
          mode="edit"
          submitLabel="Save Changes"
          cancelHref={`/apps/${id}`}
        />

        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6 sticky top-6 h-fit">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-2">
            Keep Settings in Sync
          </h2>
          <p className="text-gray-600 dark:text-dark-text-secondary">
            The Client ID, Client Secret, and Scopes here should match your app configuration in{" "}
            <a
              href="https://dev.shopify.com/dashboard"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-500 hover:text-primary-600 dark:text-primary-400"
            >
              dev.shopify.com/dashboard
            </a>
            . If you rotate your Client Secret in Shopify, update it here as well.
          </p>

          <div className="pt-4 mt-4 border-t border-blue-200 dark:border-blue-700">
            <p className="font-semibold text-gray-900 dark:text-dark-text mb-1">Scope Changes</p>
            <p className="text-gray-600 dark:text-dark-text-secondary mb-3">
              When changing scopes, update Shopify first, then update here:
            </p>
            <ol className="text-gray-600 dark:text-dark-text-secondary space-y-2 list-decimal list-inside">
              <li>Update scopes in Shopify Dev Dashboard</li>
              <li>Release a new version in Shopify</li>
              <li>Update scopes here to match</li>
              <li>
                Existing installs will show a{" "}
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 align-middle">
                  <TriangleAlert className="w-3 h-3" />
                  Needs Update
                </span>{" "}
                badge &mdash; click it to re-authorize
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
